import React, { useEffect, useState, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  message, 
  Space, 
  Tag, 
  Input, 
  Form, 
  Upload, 
  Card, 
  Row, 
  Col,
  Tooltip,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  CopyOutlined,
  EyeOutlined,
  UploadOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { apiService } from '../services/api';
import { StorageFileDTO, ResponsePageStorageFileDTO, StorageFileInfo, StorageLink, AddLink } from '../types/auth';
import { useNavigate } from 'react-router-dom';

const { Search } = Input;

const Files: React.FC = () => {
  const [filesData, setFilesData] = useState<ResponsePageStorageFileDTO>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 10
  });
  const [loading, setLoading] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadForm] = Form.useForm();
  const navigate = useNavigate();

  // Состояния для модального окна деталей файла
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [fileDetails, setFileDetails] = useState<StorageFileInfo | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [isAddLinkModalVisible, setIsAddLinkModalVisible] = useState(false);
  const [addLinkForm] = Form.useForm();
  const [addLinkLoading, setAddLinkLoading] = useState(false);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getFiles(
        currentPage,
        pageSize,
        searchQuery || undefined
      );
      setFilesData(data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        message.error('Failed to load files');
        setFilesData({
          content: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 10
        });
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, navigate]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) setPageSize(size);
  };

  // Функция для извлечения расширения файла
  const getFileExtension = (fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
  };

  // Функция для добавления расширения к имени файла, если его нет
  const ensureFileExtension = (fileName: string, file: File): string => {
    const fileExtension = getFileExtension(fileName);
    const originalExtension = getFileExtension(file.name);
    
    // Всегда добавляем расширение из оригинального файла, если его нет в имени
    if (!fileExtension && originalExtension) {
      return fileName + originalExtension;
    }
    
    // Если расширение есть, но оно отличается от оригинального, заменяем на оригинальное
    if (fileExtension && originalExtension && fileExtension !== originalExtension) {
      const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
      return nameWithoutExtension + originalExtension;
    }
    
    return fileName;
  };

  const handleUpload = async (values: { name: string; description: string; file: any }) => {
    console.log('handleUpload values:', values);
    
    if (!values.file) {
      message.error('Please select a file');
      return;
    }

    // Проверяем, является ли file массивом или одиночным объектом
    const fileItem = Array.isArray(values.file) ? values.file[0] : values.file;
    
    if (!fileItem) {
      message.error('Please select a file');
      return;
    }
    console.log('fileItem:', fileItem);
    
    let file: File;

    // Проверяем разные возможные структуры файла
    if (fileItem.originFileObj) {
      file = fileItem.originFileObj;
    } else if (fileItem instanceof File) {
      file = fileItem;
    } else if (fileItem.file) {
      file = fileItem.file;
    } else if (fileItem.uid) {
      // Это объект UploadFile, пробуем получить файл
      file = fileItem as any;
    } else {
      console.error('Unexpected file structure:', fileItem);
      message.error('Invalid file format');
      return;
    }

    if (!file) {
      message.error('No file selected');
      return;
    }

    console.log('Final file object:', file);
    console.log('File type:', typeof file);
    console.log('File constructor:', file.constructor.name);

    // Автоматически добавляем расширение к имени файла, если его нет
    const fileNameWithExtension = ensureFileExtension(values.name, file);
    console.log('Original name:', values.name);
    console.log('Original file name:', file.name);
    console.log('Name with extension:', fileNameWithExtension);

    setUploadLoading(true);

    try {
      await apiService.uploadFile(file, fileNameWithExtension, values.description);
      message.success('File uploaded successfully');
      setIsUploadModalVisible(false);
      uploadForm.resetFields();
      loadFiles();
    } catch (error: any) {
      message.error('Failed to upload file');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      await apiService.deleteFile(fileId);
      message.success('File deleted successfully');
      loadFiles();
    } catch (error: any) {
      message.error('Failed to delete file');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Link copied to clipboard');
  };

  // Функция для открытия модального окна с деталями файла
  const handleShowDetails = async (fileId: string) => {
    setDetailsLoading(true);
    try {
      const details = await apiService.getFileDetails(fileId);
      setFileDetails(details);
      setIsDetailsModalVisible(true);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        message.error('Failed to load file details');
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  // Функция для удаления ссылки
  const handleDeleteLink = async (linkId: string) => {
    try {
      await apiService.deleteLink(linkId);
      message.success('Link deleted successfully');
      // Обновляем детали файла
      if (fileDetails) {
        const updatedDetails = await apiService.getFileDetails(fileDetails.fileDTO.id);
        setFileDetails(updatedDetails);
      }
    } catch (error) {
      message.error('Failed to delete link');
    }
  };

  // Функция для добавления новой ссылки
  const handleAddLink = async (values: AddLink) => {
    if (!fileDetails) return;
    
    setAddLinkLoading(true);
    try {
      await apiService.addLink(fileDetails.fileDTO.id, values);
      message.success('Link created successfully');
      setIsAddLinkModalVisible(false);
      addLinkForm.resetFields();
      
      // Обновляем детали файла
      const updatedDetails = await apiService.getFileDetails(fileDetails.fileDTO.id);
      setFileDetails(updatedDetails);
    } catch (error) {
      message.error('Failed to create link');
    } finally {
      setAddLinkLoading(false);
    }
  };

  // Функция для обработки клика по Telegram
  const handleTelegramClick = (tgLink: string) => {
    Modal.confirm({
      title: 'Subscribe to Telegram?',
      content: 'Do you want to open the Telegram link?',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        window.open(tgLink, '_blank');
      },
    });
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'queued':
        return 'orange';
      case 'error':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'uploaded':
        return 'Uploaded';
      case 'in_progress':
        return 'In Progress';
      case 'queued':
        return 'Queued';
      case 'error':
        return 'Error';
      default:
        return status;
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <div style={{ fontWeight: 'bold' }}>{text}</div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <div 
          style={{ 
            maxWidth: 200, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={text}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'Downloads',
      dataIndex: 'downloadCount',
      key: 'downloadCount',
      render: (count: number) => (
        <Tag color="blue">{count}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: StorageFileDTO) => (
        <Space>
          <Tooltip title="File details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleShowDetails(record.id)}
              loading={detailsLoading}
            />
          </Tooltip>
          {record.publicUrl && (
            <Tooltip title="Copy public URL">
              <Button
                type="text"
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copyToClipboard(record.publicUrl!)}
              />
            </Tooltip>
          )}
          <Popconfirm
            title="Are you sure you want to delete this file?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (dateTime: string) => formatDateTime(dateTime),
    },

  ];

  return (
    <div>
      <Card title="File Management" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12}>
            <Search
              placeholder="Search files..."
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsUploadModalVisible(true)}
            >
              Upload File
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={filesData.content}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: filesData.totalElements,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} items`,
          onChange: handlePageChange,
        }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title="Upload New File"
        open={isUploadModalVisible}
        onCancel={() => {
          setIsUploadModalVisible(false);
          uploadForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={uploadForm}
          layout="vertical"
          onFinish={handleUpload}
        >
          <Form.Item
            name="name"
            label="File Name"
            rules={[{ required: true, message: 'Please enter file name' }]}
            extra="Extension will be automatically added from the selected file, even if you remove it"
          >
            <Input placeholder="Enter file name (extension will be added automatically)" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input.TextArea 
              placeholder="Enter file description" 
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="file"
            label="File"
            rules={[{ required: true, message: 'Please select a file' }]}
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
          >
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              accept="*/*"
              onChange={(info) => {
                console.log('Upload onChange info:', info);
                if (info.fileList.length > 0) {
                  const file = info.fileList[0];
                  const fileName = file.name;
                  
                  // Если поле имени файла пустое, автоматически заполняем его
                  const currentName = uploadForm.getFieldValue('name');
                  if (!currentName || currentName.trim() === '') {
                    // Убираем расширение из имени файла для поля ввода
                    const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
                    uploadForm.setFieldValue('name', nameWithoutExtension);
                  }
                }
              }}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  setIsUploadModalVisible(false);
                  uploadForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={uploadLoading}
              >
                Upload
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно с деталями файла */}
      <Modal
        title="File Details"
        open={isDetailsModalVisible}
        onCancel={() => {
          setIsDetailsModalVisible(false);
          setFileDetails(null);
        }}
        footer={null}
        width={800}
      >
        {fileDetails && (
          <div>
            {/* Информация о файле */}
            <Card title="File Information" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <strong>Name:</strong> {fileDetails.fileDTO.name}
                </Col>
                <Col span={12}>
                  <strong>Downloads:</strong> {fileDetails.fileDTO.downloadCount}
                </Col>
                <Col span={24}>
                  <strong>Description:</strong> {fileDetails.fileDTO.description}
                </Col>
                <Col span={12}>
                  <strong>Status:</strong> 
                  <Tag color={getStatusColor(fileDetails.fileDTO.state)} style={{ marginLeft: 8 }}>
                    {getStatusText(fileDetails.fileDTO.state)}
                  </Tag>
                </Col>
                <Col span={12}>
                  <strong>Created:</strong> {formatDateTime(fileDetails.fileDTO.createdAt)}
                </Col>
                {fileDetails.fileDTO.publicUrl && (
                  <Col span={24}>
                    <strong>Public Url:</strong>
                    <Space style={{ marginLeft: 8, marginTop: 8 }}>
                      <a 
                        href={fileDetails.fileDTO.publicUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ wordBreak: 'break-all' }}
                      >
                        {fileDetails.fileDTO.publicUrl}
                      </a>
                      <Tooltip title="Copy public URL">
                        <Button
                          type="text"
                          icon={<CopyOutlined />}
                          size="small"
                          onClick={() => copyToClipboard(fileDetails.fileDTO.publicUrl!)}
                        />
                      </Tooltip>
                    </Space>
                  </Col>
                )}
              </Row>
            </Card>

            {/* Кнопки действий */}
            <Card title="Actions" style={{ marginBottom: 16 }}>
              <Space>
                {fileDetails.fileDTO.previewUrl && (
                  <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => window.open(fileDetails.fileDTO.previewUrl, '_blank')}
                  >
                    Preview
                  </Button>
                )}
                <Popconfirm
                  title="Are you sure you want to delete this file?"
                  onConfirm={() => {
                    handleDelete(fileDetails.fileDTO.id);
                    setIsDetailsModalVisible(false);
                    setFileDetails(null);
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </Space>
            </Card>

            {/* Список ссылок */}
            <Card 
              title="Links" 
              extra={
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={() => setIsAddLinkModalVisible(true)}
                >
                  Add Link
                </Button>
              }
            >
              {fileDetails.links.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                  No links created yet
                </div>
              ) : (
                <Table
                  dataSource={fileDetails.links}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      key: 'name',
                      width: '40%',
                      render: (text: string) => (
                        <div style={{ 
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.4'
                        }}>
                          {text}
                        </div>
                      ),
                    },
                    {
                      title: 'Downloads',
                      dataIndex: 'downloadCount',
                      key: 'downloadCount',
                      width: '15%',
                      render: (count: number) => (
                        <Tag color="blue">{count}</Tag>
                      ),
                    },
                    {
                      title: 'Crm Lead ID',
                      dataIndex: 'leadCrmId',
                      key: 'leadCrmId',
                      width: '25%',
                      render: (id: string) => id || '-',
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: '20%',
                      render: (record: StorageLink) => (
                        <Space>
                          <Tooltip title="Copy link">
                            <Button
                              type="text"
                              icon={<CopyOutlined />}
                              size="small"
                              onClick={() => copyToClipboard(record.url)}
                            />
                          </Tooltip>
                          {record.tgLink && (
                            <Tooltip title="Telegram">
                              <Button
                                type="text"
                                size="small"
                                onClick={() => handleTelegramClick(record.tgLink!)}
                                style={{ padding: '4px' }}
                              >
                                <img 
                                  src="/telegram-svgrepo-com.svg" 
                                  alt="Telegram" 
                                  style={{ width: '16px', height: '16px' }}
                                />
                              </Button>
                            </Tooltip>
                          )}
                          <Popconfirm
                            title="Are you sure you want to delete this link?"
                            onConfirm={() => handleDeleteLink(record.id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              size="small"
                            />
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
              )}
            </Card>
          </div>
        )}
      </Modal>

      {/* Модальное окно для создания новой ссылки */}
      <Modal
        title="Create New Link"
        open={isAddLinkModalVisible}
        onCancel={() => {
          setIsAddLinkModalVisible(false);
          addLinkForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={addLinkForm}
          layout="vertical"
          onFinish={handleAddLink}
        >
          <Form.Item
            name="name"
            label="Link Name"
            rules={[{ required: true, message: 'Please enter link name' }]}
          >
            <Input placeholder="Enter link name" />
          </Form.Item>

          <Form.Item
            name="crmLeadId"
            label="Crm Lead Id (optional)"
            extra="Enter Crm Lead ID or link"
          >
            <Input placeholder="Enter Crm Lead ID or link" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  setIsAddLinkModalVisible(false);
                  addLinkForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={addLinkLoading}
              >
                Create Link
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Files;
